import { useMemo } from 'react';

import {
  MinimalDeviceCategoryEnum,
  MinimalDeviceImportanceEnum,
  MinimalDeviceSourcesEnum,
} from '@rtkwlf/asset-data-service';
import { useTranslation } from 'react-i18next';

import { useTags } from '@apis/asset-data-service/queries/use-tags';

import {
  ASSET_COLUMNS_DEFS,
  type AssetsTableColumnId,
} from '../asset-table.constants';

export const FILTER_VALUES = {
  [ASSET_COLUMNS_DEFS.IMPORTANCE]: Object.values(MinimalDeviceImportanceEnum),
  [ASSET_COLUMNS_DEFS.CATEGORY]: Object.values(
    MinimalDeviceCategoryEnum,
    // TODO: Mobile is not supported yet by backend. Remove this filter once 'mobile' is supported in a future version of ADS
  ).filter((value) => value !== 'mobile'),
  [ASSET_COLUMNS_DEFS.SOURCES]: [
    MinimalDeviceSourcesEnum.Agent,
    MinimalDeviceSourcesEnum.Iva,
  ],
};

export const FILTER_VALUES_TRANSLATION_PREFIXES = {
  [ASSET_COLUMNS_DEFS.IMPORTANCE]: 'importance',
  [ASSET_COLUMNS_DEFS.CATEGORY]: 'category',
  [ASSET_COLUMNS_DEFS.SOURCES]: 'source',
} as const satisfies Partial<Record<AssetsTableColumnId, string>>;

/**
 * Converts values to filter options with translated labels for the assets table.
 * @param {string[]} values - The filter values to be translated.
 * @param {string} keyPrefix - The prefix for the translation keys.
 * @param {function} t - The translation function.
 * @returns {object[]} The translated filter options.
 */
export const translateOptions = (
  values: string[],
  keyPrefix: string,
  t: (key: string) => string,
) => {
  return values.map((value) => ({
    label: t(`${keyPrefix}.${value}`),
    value,
  }));
};

/**
 * Returns the filter options for the assets table.
 * The filter options are translated using the provided translation function.
 * The filter options are memoized to avoid unnecessary re-renders.
 * @returns {object} The filter options for the assets table with keys as `ASSET_COLUMNS_DEFS` values.
 */
export const useAssetFilterOptions = () => {
  const { t } = useTranslation('ads');
  const { data, isFetching } = useTags();
  const filterOptions = useMemo(() => {
    return {
      [ASSET_COLUMNS_DEFS.IMPORTANCE]: translateOptions(
        FILTER_VALUES[ASSET_COLUMNS_DEFS.IMPORTANCE],
        FILTER_VALUES_TRANSLATION_PREFIXES[ASSET_COLUMNS_DEFS.IMPORTANCE],
        t,
      ),
      [ASSET_COLUMNS_DEFS.CATEGORY]: translateOptions(
        FILTER_VALUES[ASSET_COLUMNS_DEFS.CATEGORY],
        FILTER_VALUES_TRANSLATION_PREFIXES[ASSET_COLUMNS_DEFS.CATEGORY],
        t,
      ),
      [ASSET_COLUMNS_DEFS.TAGS]: [...(data ?? new Set<string>())],
      [ASSET_COLUMNS_DEFS.SOURCES]: translateOptions(
        FILTER_VALUES[ASSET_COLUMNS_DEFS.SOURCES],
        FILTER_VALUES_TRANSLATION_PREFIXES[ASSET_COLUMNS_DEFS.SOURCES],
        t,
      ),
    };
  }, [t, data]);

  return { filterOptions, isFetching };
};
